import React, { useState } from 'react';
import { useMarketSummary, useVolumeHistory, useListingsUnderSummary } from '../../hooks/useApi/market';
import { useActiveListings } from '../../hooks/useActiveListings';
import KPICard from './shared/KPICard';
import VolumeChart from './shared/VolumeChart';
import ErrorBoundary from './shared/ErrorBoundary';

const OverviewTab = () => {
  const [volumeWindow, setVolumeWindow] = useState('30d');
  
  const { data: marketSummary, isLoading: summaryLoading, isError: summaryError } = useMarketSummary();
  const { data: volumeHistory, isLoading: volumeLoading, isError: volumeError } = useVolumeHistory(volumeWindow, volumeWindow === '30d' ? 'day' : 'hour');
  const { data: listingsUnder, isLoading: listingsLoading, isError: listingsError } = useListingsUnderSummary();
  const { data: activeListings, isLoading: activeListingsLoading, isError: activeListingsError } = useActiveListings();

  // Debug logging for active listings
  console.log('🔍 OverviewTab - Active Listings State:', {
    activeListings,
    activeListingsLoading,
    activeListingsError,
    count: activeListings?.count
  });


  const formatXTZ = (mutez) => {
    if (!mutez) return '0';
    return (mutez / 1000000).toFixed(2);
  };

  const formatUSD = (mutez, rate) => {
    if (!mutez || !rate) return null;
    return `≈ $${((mutez / 1000000) * rate).toFixed(2)}`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  // Helper function to calculate volume from trades events
  const calculateVolume = (tradesData) => {
    if (!tradesData?.events) return 0;
    return tradesData.events.reduce((total, event) => {
      return total + (event.sales?.price || 0);
    }, 0);
  };

  // Helper function to get sales count
  const getSalesCount = (tradesData) => {
    return tradesData?.count || 0;
  };

  // Helper function to get listings count
  const getListingsCount = (listingsData) => {
    return listingsData?.count || 0;
  };

  const getTrendIcon = (change) => {
    if (!change) return '';
    return change > 0 ? '↗' : change < 0 ? '↘' : '→';
  };

  const getTrendType = (change) => {
    if (!change) return 'neutral';
    return change > 0 ? 'positive' : change < 0 ? 'negative' : 'neutral';
  };

  return (
    <ErrorBoundary>
      <div className="overview-tab">
        {/* Key Metrics Dashboard */}
        <div className="metrics-dashboard">
          {/* Primary Metrics Row */}
          <div className="primary-metrics-row">
            <div className="metric-card featured">
              <div className="metric-header">
                <h3 className="metric-title">Floor Price</h3>
              </div>
              <div className="metric-value">
                {marketSummary?.floor?.price ? `${formatXTZ(marketSummary.floor.price)} XTZ` : 'Loading...'}
              </div>
              {/* {marketSummary?.floor?.price_usd && (
                <div className="metric-subtitle">≈ ${(marketSummary.floor.price_usd).toFixed(2)}</div>
              )} */}
            </div>

            <div className="metric-card">
              <div className="metric-header">
                <h3 className="metric-title">Active Listings</h3>
              </div>
              <div className="metric-value">
                {activeListingsLoading ? 'Loading...' : activeListingsError ? 'Error' : formatNumber(activeListings?.count || 0)}
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-header">
                <h3 className="metric-title">Unique Holders</h3>
              </div>
              <div className="metric-value">
                {marketSummary?.holders?.unique ? formatNumber(marketSummary.holders.unique) : 'Loading...'}
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-header">
                <h3 className="metric-title">Top 10 Share</h3>
              </div>
              <div className="metric-value">
                {marketSummary?.holders?.top10 ? `${((marketSummary.holders.top10.reduce((total, holder) => total + holder.quantity, 0) / 5000) * 100).toFixed(1)}%` : 'Loading...'}
              </div>
            </div>
          </div>

          {/* Secondary Metrics Grid */}
          <div className="secondary-metrics-grid">
            <div className="metrics-section">
              <h3 className="section-title">Volume Analysis</h3>
              <div className="metrics-row">
                <div className="metric-item">
                  <span className="metric-label">24h Volume</span>
                  <span className="metric-number">
                    {marketSummary?.trades?.["24h"] ? `${formatXTZ(calculateVolume(marketSummary.trades["24h"]))} XTZ` : 'Loading...'}
                  </span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">7d Volume</span>
                  <span className="metric-number">
                    {marketSummary?.trades?.["7d"] ? `${formatXTZ(calculateVolume(marketSummary.trades["7d"]))} XTZ` : 'Loading...'}
                  </span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">30d Volume</span>
                  <span className="metric-number">
                    {marketSummary?.trades?.["30d"] ? `${formatXTZ(calculateVolume(marketSummary.trades["30d"]))} XTZ` : 'Loading...'}
                  </span>
                </div>
              </div>
            </div>

            <div className="metrics-section">
              <h3 className="section-title">Sales Activity</h3>
              <div className="metrics-row">
                <div className="metric-item">
                  <span className="metric-label">24h Sales</span>
                  <span className="metric-number">
                    {marketSummary?.trades?.["24h"] ? formatNumber(getSalesCount(marketSummary.trades["24h"])) : 'Loading...'}
                  </span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">7d Sales</span>
                  <span className="metric-number">
                    {marketSummary?.trades?.["7d"] ? formatNumber(getSalesCount(marketSummary.trades["7d"])) : 'Loading...'}
                  </span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">30d Sales</span>
                  <span className="metric-number">
                    {marketSummary?.trades?.["30d"] ? formatNumber(getSalesCount(marketSummary.trades["30d"])) : 'Loading...'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="charts-section">
          <div className="chart-container">
            <div className="chart-header">
              <h3>Volume History</h3>
              <div className="chart-controls">
                <button 
                  className={`time-button ${volumeWindow === '24h' ? 'active' : ''}`}
                  onClick={() => setVolumeWindow('24h')}
                >
                  24h
                </button>
                <button 
                  className={`time-button ${volumeWindow === '7d' ? 'active' : ''}`}
                  onClick={() => setVolumeWindow('7d')}
                >
                  7d
                </button>
                <button 
                  className={`time-button ${volumeWindow === '30d' ? 'active' : ''}`}
                  onClick={() => setVolumeWindow('30d')}
                >
                  30d
                </button>
              </div>
            </div>
            <div className="chart-content">
            <VolumeChart 
              data={volumeHistory ? volumeHistory.map(item => ({
                ...item,
                value: item.volume || 0
              })) : []} 
              loading={volumeLoading}
              className="volume-chart"
            />
            </div>
          </div>

          {listingsUnder && (
            <div className="chart-container">
              <div className="chart-header">
                <h3>Price Distribution</h3>
              </div>
              <div className="chart-content">
                <div className="price-distribution">
                {Object.entries(listingsUnder).map(([range, count]) => (
                    <div key={range} className="price-bar">
                      <div className="price-label">
                      {range === 'under_10' ? 'Under 10 XTZ' :
                       range === '10_20' ? '10-20 XTZ' :
                       range === '20_30' ? '20-30 XTZ' :
                       range === '30_40' ? '30-40 XTZ' :
                       range === '40_50' ? '40-50 XTZ' :
                       range === '50_100' ? '50-100 XTZ' :
                       range === '100_200' ? '100-200 XTZ' :
                       range === '200_300' ? '200-300 XTZ' :
                       range === '300_400' ? '300-400 XTZ' :
                       range === '400_500' ? '400-500 XTZ' :
                       range === '500_1000' ? '500-1000 XTZ' :
                       range === 'above_1000' ? 'Above 1000 XTZ' :
                       range}
                    </div>
                      <div className="price-bar-content">
                        <div className="price-bar-fill" style={{ width: `${(count / Math.max(...Object.values(listingsUnder))) * 100}%` }}></div>
                        <div className="price-count">{formatNumber(count)}</div>
                      </div>
                  </div>
                ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default OverviewTab;
